/**
 * Thin progress bar that shows scroll completion percentage at the top of the landing page.
 *
 * What: A fixed position indicator that fills from left to right as the user scrolls down the page.
 * Why: Provides visual feedback on page progress and encourages exploration of all content.
 * When: Update when changing progress bar styling or scroll behavior.
 */
import React, { useCallback, useEffect, useRef } from 'react';
import { Box } from '@mui/material';

import { colors } from './landingTokens.js';

function ScrollProgressBar() {
  const barRef = useRef<HTMLDivElement>(null);
  const rafId = useRef(0);

  const updateProgress = useCallback(() => {
    const el = barRef.current;
    if (!el) return;
    const maxScroll =
      document.documentElement.scrollHeight - window.innerHeight;
    const progress = maxScroll > 0 ? window.scrollY / maxScroll : 0;
    // scaleX instead of width: transforms stay on the compositor, so the
    // scrollHeight read above never hits a layout invalidated by our own
    // previous frame's write (forced reflow on every scroll tick).
    el.style.transform = `scaleX(${Math.min(progress, 1)})`;
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(updateProgress);
    };

    // Set initial scroll position
    updateProgress();

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId.current);
    };
  }, [updateProgress]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        zIndex: 1300, // Above most content but below modals
        pointerEvents: 'none',
      }}
    >
      <Box
        ref={barRef}
        sx={{
          height: '100%',
          width: '100%',
          transform: 'scaleX(0)',
          transformOrigin: 'left',
          backgroundColor: colors.accentOrange,
          transition: 'transform 0.1s ease-out',
          boxShadow: `0 0 8px ${colors.accentOrange}`,
          willChange: 'transform',
        }}
      />
    </Box>
  );
}

export default ScrollProgressBar;
